import React from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress, Box } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * MuiLoadingButton - Button with loading state
 *
 * Jakob's Law: Standard button behavior with loading feedback
 * Von Restorff Effect: Primary color for main CTAs, error for destructive
 */

const StyledButton = styled(Button)(({ theme, customcolor }) => {
  const colorMap = {
    primary: {
      main: theme.palette.primary.main,
      contrastText: '#fff',
      hover: theme.palette.primary.dark,
    },
    secondary: {
      main: theme.palette.secondary.main,
      contrastText: '#fff',
      hover: theme.palette.secondary.dark,
    },
    success: {
      main: theme.palette.success.main,
      contrastText: '#fff',
      hover: theme.palette.success.dark,
    },
    warning: {
      main: theme.palette.warning.main,
      contrastText: '#fff',
      hover: theme.palette.warning.dark,
    },
    error: {
      main: theme.palette.error.main,
      contrastText: '#fff',
      hover: theme.palette.error.dark,
    },
  };

  const color = colorMap[customcolor] || colorMap.primary;

  return {
    borderRadius: 6,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    padding: theme.spacing(1, 2.5),
    minWidth: 100,
    transition: 'all 0.2s ease-in-out',
    position: 'relative',
    '&.MuiButton-contained': {
      boxShadow: 'none',
      '&:hover': {
        boxShadow: `0 4px 12px ${color.main}40`,
      },
    },
    '&.MuiButton-sizeSmall': {
      padding: theme.spacing(0.5, 1.5),
      fontSize: '0.8125rem',
      minWidth: 80,
    },
    '&.MuiButton-sizeLarge': {
      padding: theme.spacing(1.25, 3),
      fontSize: '0.9375rem',
      minWidth: 120,
    },
    '&.Mui-disabled': {
      opacity: 0.65,
    },
  };
});

const LoadingWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
});

const ContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'loading',
})(({ loading }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  visibility: loading ? 'hidden' : 'visible',
}));

const MuiLoadingButton = ({
  children,
  loading = false,
  loadingPosition = 'center',
  loadingIndicator,
  disabled = false,
  variant = 'contained',
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  startIcon,
  endIcon,
  type = 'button',
  onClick,
  sx = {},
  ...props
}) => {
  const isDisabled = disabled || loading;

  const spinnerSize = {
    small: 16,
    medium: 20,
    large: 24,
  }[size];

  const spinnerColor = variant === 'contained' ? 'inherit' : color;

  const defaultLoadingIndicator = (
    <CircularProgress
      size={spinnerSize}
      color={spinnerColor}
      sx={{
        color: variant === 'contained' ? 'inherit' : undefined,
      }}
    />
  );

  const renderContent = () => {
    if (loadingPosition === 'start' && loading) {
      return (
        <ContentWrapper loading={false}>
          {loadingIndicator || defaultLoadingIndicator}
          {children}
        </ContentWrapper>
      );
    }

    if (loadingPosition === 'end' && loading) {
      return (
        <ContentWrapper loading={false}>
          {children}
          {loadingIndicator || defaultLoadingIndicator}
        </ContentWrapper>
      );
    }

    return (
      <>
        {loading && (
          <LoadingWrapper>
            {loadingIndicator || defaultLoadingIndicator}
          </LoadingWrapper>
        )}
        <ContentWrapper loading={loading}>
          {startIcon}
          {children}
          {endIcon}
        </ContentWrapper>
      </>
    );
  };

  return (
    <StyledButton
      variant={variant}
      color={color}
      customcolor={color}
      size={size}
      fullWidth={fullWidth}
      disabled={isDisabled}
      type={type}
      onClick={onClick}
      startIcon={loadingPosition === 'center' ? undefined : startIcon}
      endIcon={loadingPosition === 'center' ? undefined : endIcon}
      sx={sx}
      {...props}
    >
      {renderContent()}
    </StyledButton>
  );
};

MuiLoadingButton.propTypes = {
  /** Button content */
  children: PropTypes.node.isRequired,
  /** Loading state */
  loading: PropTypes.bool,
  /** Position of loading indicator */
  loadingPosition: PropTypes.oneOf(['start', 'center', 'end']),
  /** Custom loading indicator */
  loadingIndicator: PropTypes.node,
  /** Disabled state */
  disabled: PropTypes.bool,
  /** Button variant */
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  /** Button color */
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'inherit']),
  /** Button size */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Full width button */
  fullWidth: PropTypes.bool,
  /** Icon before text */
  startIcon: PropTypes.node,
  /** Icon after text */
  endIcon: PropTypes.node,
  /** Button type */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Click handler */
  onClick: PropTypes.func,
  /** Custom styles */
  sx: PropTypes.object,
};

export default MuiLoadingButton;
